import { Flex, Text } from '@backstage/ui';
import { formatFactKey } from '../../utils/formatFactKey';
import styles from './CheckConditions.module.css';

interface Condition {
  fact: string;
  operator: string;
  value: unknown;
}

interface SubCondition {
  field: string;
  op: string;
  value?: unknown;
  min?: unknown;
  max?: unknown;
}

type ConditionOrGroup =
  | Condition
  | { all?: ConditionOrGroup[]; any?: ConditionOrGroup[] };

interface ConditionGroup {
  all?: ConditionOrGroup[];
  any?: ConditionOrGroup[];
}

function isLeafCondition(item: ConditionOrGroup): item is Condition {
  return 'fact' in item;
}

const OP_LABEL: Record<string, string> = {
  equal: '=',
  notEqual: '≠',
  lessThan: '<',
  lessThanInclusive: '≤',
  greaterThan: '>',
  greaterThanInclusive: '≥',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  contains: 'contains',
  doesNotContain: 'does not contain',
  in: 'in',
  notIn: 'not in',
  equals: '=',
  startsWith: 'starts with',
  endsWith: 'ends with',
  semverLt: '<',
  semverLte: '≤',
  semverGt: '>',
  semverGte: '≥',
  semverEq: '=',
  between: 'between',
};

// For noneMatch: the check describes what would be BAD — invert to show the requirement
const INVERT_OP: Record<string, string> = {
  lt: '≥',
  lte: '>',
  gt: '≤',
  gte: '<',
  semverLt: '≥',
  semverLte: '>',
  semverGt: '≤',
  semverGte: '<',
  semverEq: '≠',
  equals: '≠',
  equal: '≠',
  contains: 'does not contain',
  startsWith: 'does not start with',
  endsWith: 'does not end with',
};

const prettyOp = (op: string): string => OP_LABEL[op] ?? op;

const prettyValue = (value: unknown): string => {
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'boolean' || typeof value === 'number')
    return String(value);
  return JSON.stringify(value);
};

const renderFilterSubject = (filter: SubCondition[]): string => {
  if (
    filter.length === 1 &&
    (filter[0].op === 'equals' || filter[0].op === 'equal')
  ) {
    return String(filter[0].value ?? '');
  }
  return filter
    .map(f => `${f.field} ${prettyOp(f.op)} ${prettyValue(f.value)}`)
    .join(' & ');
};

const renderCheckConditions = (
  check: SubCondition[],
  invert: boolean,
): string =>
  check
    .map(c => {
      if (c.op === 'between') {
        const betweenOp = invert ? 'not between' : 'between';
        return `${c.field} ${betweenOp} ${c.min} and ${c.max}`;
      }
      const op = invert
        ? INVERT_OP[c.op] ?? `not ${prettyOp(c.op)}`
        : prettyOp(c.op);
      return `${c.field} ${op} ${prettyValue(c.value)}`;
    })
    .join(' & ');

const renderConditionText = (cond: Condition): string => {
  const { fact, operator, value } = cond;
  const factLabel = formatFactKey(fact);
  const isMatchOp = ['noneMatch', 'allMatch', 'anyMatch'].includes(operator);

  if (!isMatchOp) {
    return `${factLabel}: ${prettyOp(operator)} ${prettyValue(value)}`;
  }

  if (!value || typeof value !== 'object') {
    return `${factLabel}: ${operator} ${prettyValue(value)}`;
  }

  const v = value as Record<string, unknown>;
  const invert = operator === 'noneMatch';

  if (Array.isArray(v.filter) && Array.isArray(v.check)) {
    const subject = renderFilterSubject(v.filter as SubCondition[]);
    const checkStr = renderCheckConditions(v.check as SubCondition[], invert);
    return `${factLabel} (${subject}): ${checkStr}`;
  }

  if (v.field && v.op) {
    const op = v.op as string;
    if (op === 'between') {
      const betweenOp = invert ? 'not between' : 'between';
      return `${factLabel}: ${v.field} ${betweenOp} ${v.min} and ${v.max}`;
    }
    const displayOp = invert
      ? INVERT_OP[op] ?? `not ${prettyOp(op)}`
      : prettyOp(op);
    return `${factLabel}: ${v.field} ${displayOp} ${prettyValue(v.value)}`;
  }

  return `${factLabel}: ${operator} ${prettyValue(value)}`;
};

function renderConditionList(items: ConditionOrGroup[]): React.ReactElement {
  return (
    <ul className={styles.list}>
      {items.map((item, i) => (
        <li key={i}>
          {isLeafCondition(item) ? (
            <Text
              as="span"
              variant="body-small"
              className={styles.conditionText}
            >
              {renderConditionText(item)}
            </Text>
          ) : (
            <>
              <Text
                as="span"
                variant="body-small"
                className={styles.conditionText}
              >
                {item.all ? 'All of:' : 'Any of:'}
              </Text>
              {renderConditionList(item.all ?? item.any ?? [])}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

interface CheckConditionsProps {
  checkJson: Record<string, unknown> | undefined;
}

export const CheckConditions = ({ checkJson }: CheckConditionsProps) => {
  if (!checkJson) return null;

  const conditions = checkJson.conditions as ConditionGroup | undefined;
  if (!conditions) return null;

  const allConditions = conditions.all;
  const anyConditions = conditions.any;
  const items = allConditions ?? anyConditions;
  const logic = allConditions ? 'ALL must be true' : 'ANY must be true';

  if (!items || items.length === 0) return null;

  return (
    <Flex direction="column" gap="2">
      <Text as="p" variant="body-medium" weight="bold">
        How this check works
      </Text>
      <Text as="p" variant="body-small" color="secondary">
        {logic}:
      </Text>
      {renderConditionList(items)}
    </Flex>
  );
};
